<template>
  <analytics-page-layout
    title="{organization-label}: Order Confirmation Analytics"
    heading="Order Confirmation Analytics"
    subheading="Insights into your order confirmation form entries"
    :organization_id="organization_id"
    :loading="loading"
    :is_empty="has_no_data"
  >
    <template #filters>
      <v-row
        align="start"
        :dense="$vuetify.breakpoint.smAndUp"
      >
        <v-col
          cols="12"
          sm="4"
          md="3"
        >
          <v-select
            v-model="selected_form_id"
            :items="form_options"
            label="Order Confirmation Form"
            prepend-inner-icon="mdi-form-select"
            outlined
            dense
            hide-details
            @change="loadAnalytics"
          />
        </v-col>

        <v-col
          cols="12"
          sm="5"
          md="7"
        >
          <date-range-selector
            ref="dateRangeSelector"
            v-model="date_range"
            @change="handleDateRangeChange"
          />
        </v-col>

        <v-col
          cols="12"
          sm="3"
          md="2"
        >
          <div class="d-flex justify-end align-center fill-height">
            <v-btn
              text
              color="primary"
              @click="clearFilters"
            >
              <v-icon left>mdi-filter-off</v-icon>
              Clear
            </v-btn>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="settings_modal = true"
                >
                  <v-icon>mdi-cog</v-icon>
                </v-btn>
              </template>
              <span>Display Settings</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
    </template>

    <template #content>
      <div>
        <v-tabs
          v-model="current_tab"
          background-color="transparent"
          color="primary"
        >
          <v-tab> <v-icon left>mdi-chart-bar</v-icon> Overview </v-tab>
          <v-tab> <v-icon left>mdi-check-decagram</v-icon> Consent </v-tab>
          <v-tab> <v-icon left>mdi-clock-outline</v-icon> Timing Metrics </v-tab>
        </v-tabs>

        <v-tabs-items
          v-model="current_tab"
          class="tabs-content"
        >
          <v-tab-item>
            <kpi-grid
              :kpis="overview_kpis"
              :show_zero_values="show_zero_values"
              class="mb-6"
            />
            <v-row>
              <v-col
                cols="12"
                lg="8"
              >
                <chart-card
                  title="Daily Order Confirmation Creation Trend"
                  :has_data="has_submission_time_series_data"
                  no_data_icon="mdi-chart-line"
                >
                  <m-chart-line
                    title=""
                    :labels="analytics.submission_time_series?.chart_js_labels"
                    :data="analytics.submission_time_series?.chart_js_data"
                    point_label="Order Confirmation"
                    :decimal_y_axis="false"
                    grow_chart
                  />
                </chart-card>
              </v-col>
              <v-col
                cols="12"
                lg="4"
              >
                <chart-card
                  title="Confirmations by Form"
                  :has_data="has_form_breakdown_data"
                  no_data_icon="mdi-chart-donut"
                >
                  <m-chart-donut
                    title=""
                    :labels="analytics.form_breakdown?.chart_js_labels"
                    :data="analytics.form_breakdown?.chart_js_data"
                    :table_headers="analytics.form_breakdown?.headers"
                    :table_rows="analytics.form_breakdown?.rows"
                    :colors="categorical_colors"
                  />
                </chart-card>
              </v-col>
              <v-col
                cols="12"
                lg="8"
                class="mt-6"
              >
                <chart-card
                  title="Contact Method Breakdown"
                  :has_data="has_contact_method_breakdown_data"
                  no_data_icon="mdi-chart-donut"
                >
                  <m-chart-donut
                    title=""
                    class="mt-4"
                    :labels="analytics.contact_method_breakdown?.chart_js_labels"
                    :data="analytics.contact_method_breakdown?.chart_js_data"
                    :table_headers="analytics.contact_method_breakdown?.headers"
                    :table_rows="analytics.contact_method_breakdown?.rows"
                    :colors="categorical_colors"
                  />
                </chart-card>
              </v-col>
              <v-col
                cols="12"
                lg="4"
                class="mt-6"
              >
                <chart-card
                  title="Submission States"
                  :has_data="has_state_breakdown_data"
                  no_data_icon="mdi-chart-donut"
                >
                  <m-chart-donut
                    title=""
                    :labels="analytics.state_breakdown?.chart_js_labels"
                    :data="analytics.state_breakdown?.chart_js_data"
                    :table_headers="analytics.state_breakdown?.headers"
                    :table_rows="analytics.state_breakdown?.rows"
                    :colors="categorical_colors"
                  />
                </chart-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item>
            <kpi-grid
              :kpis="consent_kpis"
              :show_zero_values="show_zero_values"
              class="mb-6"
            />
            <v-row>
              <v-col
                cols="12"
                lg="6"
              >
                <chart-card
                  title="Consent Grants"
                  :has_data="has_consent_breakdown_data"
                  no_data_icon="mdi-chart-donut"
                >
                  <m-chart-donut
                    title=""
                    :labels="analytics.consent_breakdown?.chart_js_labels"
                    :data="analytics.consent_breakdown?.chart_js_data"
                    :table_headers="analytics.consent_breakdown?.headers"
                    :table_rows="analytics.consent_breakdown?.rows"
                    :colors="binary_colors"
                  />
                </chart-card>
              </v-col>


            </v-row>
          </v-tab-item>

          <v-tab-item>
            <kpi-grid
              :kpis="timing_kpis"
              :show_zero_values="show_zero_values"
              class="mb-6"
            />
            <v-row>
              <v-col
                cols="12"
                lg="8"
              >
                <chart-card
                  title="Daily First Access Trend"
                  :has_data="has_access_time_series_data"
                  no_data_icon="mdi-chart-line"
                >
                  <m-chart-line
                    title=""
                    :labels="analytics.access_time_series?.chart_js_labels"
                    :data="analytics.access_time_series?.chart_js_data"
                    point_label="Access"
                    :decimal_y_axis="false"
                    grow_chart
                  />
                </chart-card>
              </v-col>
              <v-col
                cols="12"
                lg="4"
              >
                <chart-card
                  title="Time to First Access Distribution"
                  :has_data="has_access_time_breakdown_data"
                  no_data_icon="mdi-chart-donut"
                >
                  <m-chart-donut
                    title=""
                    :labels="analytics.access_time_breakdown?.chart_js_labels"
                    :data="analytics.access_time_breakdown?.chart_js_data"
                    :table_headers="analytics.access_time_breakdown?.headers"
                    :table_rows="analytics.access_time_breakdown?.rows"
                    :colors="timing_colors"
                  />
                </chart-card>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>

    <template #dialogs>
      <analytics-settings-dialog
        v-model="settings_modal"
        :show_zero_values.sync="show_zero_values"
      />
    </template>
  </analytics-page-layout>
</template>

<script>
import { prettyPrintDurationv2 } from '@/js/time.js';
import { CHART_COLOR_SETS, getCategoricalColors, getTimingColors } from '@/js/analytics-colors.js';
import AnalyticsPageLayout from '@/components/analytics/analytics-page-layout.vue';
import DateRangeSelector from '@/components/analytics/date-range-selector.vue';
import KpiGrid from '@/components/analytics/kpi-grid.vue';
import ChartCard from '@/components/analytics/chart-card.vue';
import AnalyticsSettingsDialog from '@/components/analytics/analytics-settings-dialog.vue';

export default {
  name: 'OrderConfirmationAnalytics',
  components: {
    AnalyticsPageLayout,
    DateRangeSelector,
    KpiGrid,
    ChartCard,
    AnalyticsSettingsDialog,
  },
  data() {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      organization_id: this.$route.params.organization_id,
      loading: true,
      analytics: {},
      date_range: {
        start: this.formatDateForPicker(thirtyDaysAgo),
        end: this.formatDateForPicker(today),
      },
      selected_form_id: null,
      forms: [],
      current_tab: null,
      settings_modal: false,
      show_zero_values: false,
    };
  },
  computed: {
    has_no_data() {
      // Check after loading and if analytics object exists
      return !this.loading && this.analytics && this.analytics.total_submissions === 0;
    },
    form_options() {
      return [
        { text: 'All Forms', value: null },
        ...(this.forms || []).map(form => ({ // Add safety check for this.forms
          text: form.internal_label,
          value: form.order_confirmation_settings_id,
        })),
      ];
    },
    overview_kpis() {
      // Check if analytics data is loaded before trying to access properties
      if (!this.analytics || Object.keys(this.analytics).length === 0) return [];
      return [
        {
          label: 'Total Confirmation Requests Created',
          value: (this.analytics.total_submissions || 0).toLocaleString(),
          tooltip: 'Total number of order confirmation requests created.',
        },
        {
          label: 'Completed Confirmations',
          value: (this.analytics.completed_submissions || 0).toLocaleString(),
          tooltip: 'Confirmations marked as "Completed" (either confirmed or update request).',
        },
        {
          label: 'Confirmed without Update Request',
          value: (this.analytics.total_confirmed || 0).toLocaleString(),
          tooltip: 'Completed entries where the customer confirmed the order details without requesting a change.',
        },
        {
          label: 'Confirmed with Update Request',
          value: (this.analytics.total_change_requests || 0).toLocaleString(),
          tooltip: 'Completed entries where the customer requested a change.',
        },
        {
          label: 'Pending Confirmations',
          value: (this.analytics.pending_submissions || 0).toLocaleString(),
          tooltip: 'Entries that are still awaiting customer action.',
        },
        {
          label: 'Completion Rate',
          value: this.formatPercent(this.analytics.completion_rate),
          tooltip: 'The percentage of total entries marked in a "Completed" state.',
        },
        {
          label: 'Update Request Rate',
          value: this.formatPercent(this.analytics.change_request_rate),
          tooltip: 'The percentage of completed entries that were update requests.',
        },
        {
          label: 'Confirmations with Email',
          value: (this.analytics.submissions_with_email || 0).toLocaleString(),
          tooltip: 'Total submissions that included an email address.',
        },
        {
          label: 'Confirmations with Both Contact Methods',
          value: this.formatPercent(this.analytics.both_contact_rate),
          tooltip: 'Percentage of submissions that included both an email and a phone number.',
        },
        {
          label: 'New Contacts Created in CRM',
          value: (this.analytics.crm_customers_created || 0).toLocaleString(),
          tooltip:
            'The number of new contact profiles created in your CRM from submissions.',
        },
        {
          label: 'Existing Contacts Updated in CRM',
          value: (this.analytics.crm_customers_updated || 0).toLocaleString(),
          tooltip:
            'The number of existing contact profiles in your CRM that were updated with new information from submissions.',
        },
      ];
    },
    consent_kpis() {
      // Check if analytics data is loaded
      if (!this.analytics || Object.keys(this.analytics).length === 0) return [];
      return [
        {
          label: 'Email Consent Granted',
          value: (this.analytics.email_consent_granted || 0).toLocaleString(),
          tooltip: 'Total number of completed submissions that granted email consent on the form.',
        },
        {
          label: 'Email Consent Rate',
          value: this.formatPercent(this.analytics.email_consent_rate),
          tooltip:
            'The percentage of completed submissions (with an email) that granted email consent on the form.',
        },
        {
          label: 'Phone Consent Granted',
          value: (this.analytics.phone_consent_granted || 0).toLocaleString(),
          tooltip: 'Total number of completed submissions that granted phone consent on the form.',
        },
        {
          label: 'Phone Consent Rate',
          value: this.formatPercent(this.analytics.phone_consent_rate),
          tooltip:
            'The percentage of completed submissions that granted phone consent on the form.',
        },

      ];
    },
    timing_kpis() {
      // Check if analytics data is loaded
      if (!this.analytics || Object.keys(this.analytics).length === 0) return [];
      return [
        {
          label: 'Confirmations Accessed',
          value: (this.analytics.confirmations_accessed || 0).toLocaleString(),
          tooltip: 'Total number of confirmations that were accessed at least once by customers.',
        },
        {
          label: 'Confirmations Not Accessed',
          value: (this.analytics.confirmations_not_accessed || 0).toLocaleString(),
          tooltip: 'Total number of confirmations that were never accessed.',
        },
        {
          label: 'Access Rate',
          value: this.formatPercent(this.analytics.access_rate),
          tooltip: 'The percentage of confirmations that were accessed by customers.',
        },
        {
          label: 'Confirmations Abandoned',
          value: (this.analytics.confirmations_abandoned || 0).toLocaleString(),
          tooltip: 'Confirmations that were accessed at least once but not submitted.',
        },
        {
          label: 'Abandonment Rate',
          value: this.formatPercent(this.analytics.abandonment_rate),
          tooltip: 'The percentage of accessed confirmations that were abandoned.',
        },
        {
          label: 'Median Time to First Access',
          value: prettyPrintDurationv2(this.analytics.median_time_to_access_ms || 0),
          tooltip: 'The median time from when the confirmation request was created to when the customer first accessed the form.',
        },
        {
          label: 'P90 Time to First Access',
          value: prettyPrintDurationv2(this.analytics.time_to_access_p90_ms || 0),
          tooltip: '90th percentile time to first access (90% of customers accessed faster than this, measured from creation).',
        },
        {
          label: 'Median Form Completion Time',
          value: prettyPrintDurationv2(this.analytics.median_time_to_submit_ms || 0),
          tooltip: 'The median time it took for customers to submit the form after they first accessed it.',
        },
        {
          label: 'P90 Form Completion Time',
          value: prettyPrintDurationv2(this.analytics.time_to_submit_p90_ms || 0),
          tooltip: '90th percentile form completion time (90% of customers submitted faster than this, measured from first access).',
        },
      ];
    },

    // Chart Data Checks - Added optional chaining ?. for safety
    has_submission_time_series_data() {
      return (
        this.analytics.submission_time_series?.chart_js_data &&
        this.analytics.submission_time_series.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },
    has_form_breakdown_data() {
      return (
        this.analytics.form_breakdown?.chart_js_data &&
        this.analytics.form_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },
    has_state_breakdown_data() {
      return (
        this.analytics.state_breakdown?.chart_js_data &&
        this.analytics.state_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },
    has_contact_method_breakdown_data() {
      return (
        this.analytics.contact_method_breakdown?.chart_js_data &&
        this.analytics.contact_method_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },
    has_consent_breakdown_data() {
      return (
        this.analytics.consent_breakdown?.chart_js_data &&
        this.analytics.consent_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },
    has_access_time_breakdown_data() {
      return (
        this.analytics.access_time_breakdown?.chart_js_data &&
        this.analytics.access_time_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },
    has_access_time_series_data() {
      return (
        this.analytics.access_time_series?.chart_js_data &&
        this.analytics.access_time_series.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    // Chart Colors
    categorical_colors() {
      const formCount = this.analytics.form_breakdown?.chart_js_data?.length || 0;
      const contactCount = this.analytics.contact_method_breakdown?.chart_js_data?.length || 0;
      const stateCount = this.analytics.state_breakdown?.chart_js_data?.length || 0;
      const maxCount = Math.max(formCount, contactCount, stateCount, 6);
      return getCategoricalColors(maxCount);
    },
    binary_colors() {
      return CHART_COLOR_SETS.binary;
    },
    timing_colors() {
      const formCount = this.analytics.form_breakdown?.chart_js_data?.length || 0;
      const completionCount = this.analytics.doi_completion_breakdown?.chart_js_data?.length || 0;
      const maxCount = Math.max(formCount, completionCount, 5);
      return getTimingColors(maxCount);
    },

    // Date Range Helpers
    time_start() {
      if (!this.date_range.start) return null;
      return new Date(`${this.date_range.start}T00:00:00Z`).getTime();
    },
    time_stop() {
      if (!this.date_range.end) return null;
      return new Date(`${this.date_range.end}T23:59:59Z`).getTime();
    },
  },
  watch: {
    show_zero_values(new_value) {
      localStorage.setItem('orderConfirmationAnalytics.showZeroValues', new_value.toString());
    },
  },
  mounted() {
    this.loadShowZeroValuesSetting();
    if (this.$route.query.order_confirmation_settings_id) {
      this.selected_form_id = this.$route.query.order_confirmation_settings_id;
    }
    this.loadForms();
    this.loadAnalytics();
  },
  methods: {
    loadShowZeroValuesSetting() {
      const saved_setting = localStorage.getItem('orderConfirmationAnalytics.showZeroValues');
      if (saved_setting !== null) {
        this.show_zero_values = saved_setting === 'true';
      }
    },
    handleDateRangeChange() {
      this.loadAnalytics();
    },
    formatDateForPicker(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    clearFilters() {
      this.selected_form_id = null;
      if (this.$refs.dateRangeSelector) {
        this.$refs.dateRangeSelector.resetToDefault();
      }
      this.loadAnalytics();
    },
    loadForms() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'POST: /api/v1/order-confirmation/settings/search',
        params: {
          organization_id: this.organization_id,
          is_published: true,
        },
        on_success: data => {
          this.forms = data.results || [];
        },
        on_error: () => {
          this.$rc.showError('Unable to load forms data'); // Added error handling
        }
      });
    },
    loadAnalytics() {
      if (!this.time_start || !this.time_stop) return;

      this.loading = true;
      this.analytics = {}; // Reset analytics on load to prevent showing stale data

      const params = {
        organization_id: this.organization_id,
        time_start: this.time_start,
        time_stop: this.time_stop,
      };

      if (this.selected_form_id) {
        params.order_confirmation_settings_id = this.selected_form_id;
      }

      // Add a minimum delay for better UX
      const minDelay = new Promise(resolve => setTimeout(resolve, 300));

      Promise.all([
        minDelay,
        this.$rc.rest({
          spinner: false, // Keep spinner off as loading state handles it
          endpoint: 'PUT: /api/v1/order-confirmation/analytics/summary',
          params,
          on_success: data => {
            // Check if data is actually returned before assigning
            if (data) {
              this.analytics = data;
            } else {
              this.analytics = {}; // Ensure it's an empty object if API returns nothing
            }
          },
          on_error: (error) => { // Added error parameter
            this.analytics = {}; // Clear data on error
            this.handleDateRangeChange();
          },
        }),
      ]).finally(() => {
        this.loading = false;
      });
    },
    formatPercent(value) {
      if (value === null || value === undefined || isNaN(value)) return '0%'; // Added NaN check
      return `${Math.round(value * 100)}%`;
    },
  },
};
</script>

<style lang="scss" scoped>
.tabs-content {
  background-color: transparent !important;
}

.chart-card {
  min-height: auto;
}

.v-row {
  align-items: start;
}
:deep(.v-tooltip__content) {
  background: rgba(33, 33, 33, 0.97);
  color: white;
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  max-width: 250px;
  text-align: left;
}
</style>